/**
 * TimelineDialog — a compact scrubber over the session's user turns.
 *
 * Jump to any past message (scrolls the conversation to it) or branch a new
 * session from that point. Reuses the existing fork ("branch") action.
 */
import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { GitBranch, CornerUpLeft } from 'lucide-react'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'

interface TimelineMessage {
  id: string
  role: string
  parts: Array<{ type: string; content?: string; cleared?: unknown }>
  timeCreated?: number
}

interface Props {
  messages: TimelineMessage[]
  onJump: (messageId: string) => void
  onBranch: (messageId: string) => void
  onClose: () => void
}

function relTime(ts?: number): string {
  if (!ts) return ''
  const s = Math.floor((Date.now() - ts) / 1000)
  if (s < 60) return `${s}s ago`
  if (s < 3600) return `${Math.floor(s / 60)}m ago`
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`
  return `${Math.floor(s / 86400)}d ago`
}

export function TimelineDialog({ messages, onJump, onBranch, onClose }: Props) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const [query, setQuery] = useState('')

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  // One entry per user turn, with its text snippet.
  const turns = useMemo(() => {
    return messages
      .filter(m => m.role === 'user')
      .map(m => ({
        id: m.id,
        time: m.timeCreated,
        // Not a cleared-file note (0353): a turn that was only a file would
        // otherwise be listed under the model's English sentence.
        text: m.parts.filter(p => p.type === 'text' && !p.cleared).map(p => p.content ?? '').join(' ').trim(),
      }))
      .filter(t => t.text) // skip empty/system turns
  }, [messages])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q ? turns.filter(t => t.text.toLowerCase().includes(q)) : turns
  }, [turns, query])

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh]">
      <div className="absolute inset-0 bg-black/50 ws-anim-fade-in" onClick={onClose} />
      {/* Frost-glass dialog — the transient-popover glass (DESIGN_DOC §10),
          same material as the debug-log viewer + context menus. */}
      <div className={`${themeClass} ws-glass-modal ws-anim-modal-in relative w-full max-w-lg mx-4 flex flex-col max-h-[70vh]`}>
        <div className="px-3.5 pt-3 pb-2 shrink-0">
          <div className="flex items-center gap-2 mb-2">
            <GitBranch size={13} className="themed-text-muted shrink-0" />
            <span className="text-sm font-medium themed-text">Timeline</span>
            <span className="ml-auto text-[10px] themed-text-muted tabular-nums">{turns.length} turn{turns.length === 1 ? '' : 's'}</span>
          </div>
          <input
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Filter turns…"
            className="themed-input ws-drive-pill w-full rounded-lg px-2.5 py-1.5 text-xs outline-none"
          />
        </div>
        <div className="overflow-y-auto px-1.5 pb-2">
          {filtered.length === 0 ? (
            <div className="px-2.5 py-3 text-xs themed-text-muted">No turns yet</div>
          ) : (
            filtered.map((t, i) => (
              <div key={t.id} className="group/tl ws-menu-item !items-start !whitespace-normal !py-2 text-xs">
                <span className="shrink-0 mt-px text-[10px] themed-text-muted tabular-nums w-4 text-right">{i + 1}</span>
                <button
                  onClick={() => { onJump(t.id); onClose() }}
                  className="min-w-0 flex-1 text-left"
                  title="Jump to this message"
                >
                  <span className="line-clamp-2 themed-text">{t.text}</span>
                  <span className="block text-[10px] themed-text-muted mt-0.5">{relTime(t.time)}</span>
                </button>
                <button
                  onClick={() => { onJump(t.id); onClose() }}
                  className="shrink-0 mt-px themed-text-muted hover:themed-text opacity-0 group-hover/tl:opacity-100 transition-opacity"
                  title="Jump to this message"
                >
                  <CornerUpLeft size={12} />
                </button>
                <button
                  onClick={() => { onBranch(t.id); onClose() }}
                  className="shrink-0 mt-px themed-text-muted hover:text-brand-400 opacity-0 group-hover/tl:opacity-100 transition-opacity"
                  title="Branch a new session from here"
                >
                  <GitBranch size={12} />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
